import type { MeResponse } from "@kontaktfix/shared";
import { Button, cn } from "@kontaktfix/ui";
import { CreditCardIcon, WarningIcon } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { daysUntil } from "@/lib/i18n";

/** The trial banner appears this many days before the end. */
const TRIAL_WARNING_DAYS = 5;

/**
 * Shown above every page when the trial is about to end, has ended, or a payment failed.
 * Administrators get a link to subscribe; editors are told to ask one.
 */
export function BillingBanner({ me }: { me: MeResponse }) {
  const { t } = useTranslation();
  const billing = me.org?.billing;
  if (!billing) return null;
  const isAdmin = me.role === "admin";
  const days = daysUntil(billing.trialEndsAt);

  let tone: "warning" | "danger";
  let text: string;
  if (billing.state === "expired" || billing.state === "canceled") {
    tone = "danger";
    text = t(billing.state === "expired" ? "billing.banner.expired" : "billing.banner.canceled");
  } else if (billing.state === "past_due") {
    tone = "warning";
    text = t("billing.banner.pastDue");
  } else if (billing.state === "trial" && days <= TRIAL_WARNING_DAYS) {
    tone = "warning";
    text = t("billing.banner.trialEnding", { count: days });
  } else {
    return null;
  }

  return (
    <div
      role="status"
      className={cn(
        "flex flex-wrap items-center justify-center gap-x-3 gap-y-2 px-4 py-2 text-[12.5px]",
        tone === "danger" ? "bg-danger-soft text-danger" : "bg-warning-soft text-warning",
      )}
    >
      <span className="flex items-center gap-2">
        <WarningIcon className="size-4 shrink-0" />
        {text} {!isAdmin && t("billing.banner.askAdmin")}
      </span>
      {isAdmin && (
        <Button size="sm" variant="secondary" asChild>
          <Link to="/settings/{-$tab}" params={{ tab: "billing" }}>
            <CreditCardIcon />
            {billing.state === "past_due" ? t("billing.banner.updatePayment") : t("billing.subscribe")}
          </Link>
        </Button>
      )}
    </div>
  );
}
